/*
 * cozyarmy spotlight — playback controller.
 *
 * Behavior:
 *  - Videos are permanently muted (muted attribute + video.muted = true).
 *  - No native controls, no mute control, no fullscreen button, and no
 *    Fullscreen API calls. Pointer interaction lives on the card wrapper.
 *  - GIF-like: EVERY card that is on screen autoplays and loops continuously,
 *    on every device class (no hover required). Cards scroll out of view are
 *    paused so off-screen video is not decoded.
 *  - Click/tap or Enter/Space still toggles a single card manually. A manual
 *    pause is honoured until the section leaves the viewport and comes back.
 *  - Pause all when the page is hidden or the section leaves the viewport.
 *  - Respects prefers-reduced-motion and Data Saver (no autoplay; posters
 *    remain and manual play still works).
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointerQuery = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

  function dataSaverEnabled() {
    return Boolean(navigator.connection && navigator.connection.saveData);
  }

  // Autoplay arbitration measures real on-screen area for every card, so pointer
  // type no longer gates playback; this query is kept only to re-arbitrate when
  // the input mode changes (for example a tablet docking a mouse).

  var sections = document.querySelectorAll('[data-cozy-spotlight]');
  if (!sections.length) { return; }

  Array.prototype.forEach.call(sections, function (section) {
    var cards = Array.prototype.slice.call(
      section.querySelectorAll('[data-cozy-spotlight-card]')
    );
    if (!cards.length) { return; }

    var rail = section.querySelector('[data-cozy-spotlight-rail]');
    var status = section.querySelector('[data-cozy-spotlight-status]');

    // Lock every video to muted + inline + looping; strip any control affordance.
    cards.forEach(function (card) {
      var video = card.querySelector('video');
      if (!video) { return; }
      video.muted = true;
      video.setAttribute('muted', '');
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.removeAttribute('controls');
      // Interaction belongs to the card, never the video element.
      video.addEventListener('click', function (e) { e.preventDefault(); });
      video.addEventListener('dblclick', function (e) { e.preventDefault(); });
    });

    var playing = null;
    var sectionVisible = true;

    function pauseCard(card) {
      var video = card.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      }
      card.classList.remove('is-playing');
      card.setAttribute('aria-pressed', 'false');
      if (playing === card) { playing = null; }
    }

    function pauseAll(exceptCard) {
      cards.forEach(function (c) {
        if (c !== exceptCard) { pauseCard(c); }
      });
    }

    function playCard(card, manual) {
      if (reducedMotion || dataSaverEnabled() || document.hidden || !sectionVisible) { return; }
      if (playing === card) { return; }
      var video = card.querySelector('video');
      if (!video) { return; }
      // a manual pause outranks autoplay until the section leaves the viewport
      if (!manual && card.dataset.userPaused === 'true') { return; }

      var attempt = video.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt.then(function () {
          playing = card;
          card.classList.add('is-playing');
          card.setAttribute('aria-pressed', 'true');
          if (status) {
            status.textContent = 'Playing video: ' + (card.getAttribute('data-label') || 'cozyarmy blanket');
          }
        }).catch(function () {
          // Autoplay blocked or interrupted — leave the poster visible.
        });
      }
    }

    cards.forEach(function (card) {
      function toggleCardPlayback(event) {
        if (event) { event.preventDefault(); }
        var video = card.querySelector('video');
        if (!video || video.paused) {
          delete card.dataset.userPaused;
          playCard(card, true);
        } else {
          // remember the deliberate pause so autoplay does not immediately undo it
          card.dataset.userPaused = 'true';
          pauseCard(card);
        }
      }
      card.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          toggleCardPlayback(event);
        }
      });
      card.addEventListener('click', toggleCardPlayback);
    });

    // Coarse pointer: scroll-snap autoplay of the most-visible card.
    var ticking = false;

    function update() {
      ticking = false;
      if (reducedMotion || dataSaverEnabled() || document.hidden || !sectionVisible || !rail) { return; }

      var railRect = rail.getBoundingClientRect();
      if (railRect.width === 0) { return; }

      // GIF-like autoplay: play EVERY card that is meaningfully on screen and pause
      // the rest. Width visibility is measured inside the rail so a card parked off
      // the horizontal rail is never played; height visibility is measured against
      // the viewport so a section scrolled past stops decoding.
      var viewportH = window.innerHeight || document.documentElement.clientHeight;

      cards.forEach(function (card) {
        var r = card.getBoundingClientRect();
        if (r.width === 0 || r.height === 0) { return; }

        var visibleWidth = Math.min(r.right, railRect.right) - Math.max(r.left, railRect.left);
        var visibleHeight = Math.min(r.bottom, viewportH) - Math.max(r.top, 0);
        var visFraction = Math.min(1, Math.max(0, visibleWidth) / r.width)
          * Math.min(1, Math.max(0, visibleHeight) / r.height);

        // 0.5 threshold: a tall 9:16 card in a short viewport can never be 60% visible
        if (visFraction >= 0.5) {
          playCard(card);
        } else {
          pauseCard(card);
        }
      });
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    rail.addEventListener('scroll', requestUpdate, { passive: true });
    // Page scroll must also re-arbitrate: the section observer only fires on the
    // enter/exit crossing, so without this a card that becomes fully visible later
    // in the same scroll gesture would never start playing.
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', function () {
      cards.forEach(pauseCard);
      requestUpdate();
    }, { passive: true });
    if (finePointerQuery.addEventListener) {
      finePointerQuery.addEventListener('change', function () {
        cards.forEach(pauseCard);
        requestUpdate();
      });
    }

    // Initial arbitration (runs after layout, so poster/cards are measured).
    requestUpdate();

    // Pause everything when the section leaves the viewport.
    if ('IntersectionObserver' in window) {
      var sectionObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          sectionVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            requestUpdate();
          } else {
            // leaving the viewport clears manual pauses, so scrolling back in
            // restores the GIF-like autoplay for every card
            cards.forEach(function (c) {
              delete c.dataset.userPaused;
              pauseCard(c);
            });
          }
        });
      }, { threshold: 0 });
      sectionObserver.observe(section);
    }

    // Pause everything when the page is hidden.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        cards.forEach(pauseCard);
      } else {
        requestUpdate();
      }
    });
  });
})();
